import { useRef, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Icon from '@material-ui/core/Icon'
import Collapse from '@material-ui/core/Collapse'
import { toast } from 'react-toastify'
import { v4 as randomUUID } from 'uuid'

import useCurrentChatId from '@/components/hooks/use-current-chat-id'
import { useSocket } from '@/components/hooks/use-socket'
import useConversationsStore from '@/stores/useConversationsStore'
import { ChatMode, MessageSender, PromptMode } from '../../models/Conversation.model'

import PromptTextArea, { PromptTextAreaHandle } from './prompt-text-area'
import ActionButtonsRow from './action-buttons-row'
import AttachmentsContainer, { AttachmentsContainerHandle } from './attachments-container'
import { useAwsCredentialsManager } from '../../context/AWS-credentials.context'

const useStyles = makeStyles((theme) => ({
  container: {
    borderTop: `1px dashed ${theme.palette.grey[300]}`,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
  },
  inputWrapper: {
    display: 'flex',
    position: 'relative',
    width: '100%',
  },
  sendButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 36,
    height: 36,
    padding: 0,
    borderRadius: theme.shape.borderRadius,
  },
  uploadButton: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    background: 'none',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    cursor: 'pointer',
    padding: 8,
    transition: 'all 0.2s ease-in-out 100ms',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& svg': {
      fontSize: '1.2rem',
    },
  },
  disclaimer: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: -theme.spacing(1),
    textAlign: 'center',
  },
}))

const PromptInputArea = () => {
  const classes = useStyles()

  const navigate = useNavigate()
  const location = useLocation()

  const { isAuthorized } = useAwsCredentialsManager()

  const [promptMode, setPromptMode] = useState<PromptMode>(PromptMode.DOCUMENT_ANALYSIS)
  const [promptText, setPromptText] = useState('')

  const currentConversationId = useCurrentChatId()
  const currentConversation = useConversationsStore((s) => s.getConversation(currentConversationId))
  const parentMessageId = useConversationsStore(
    (s) => s.getLastBotMessage(currentConversationId)?.id
  )
  const pendingFirstMessage = useConversationsStore((s) => s.pendingFirstMessage)
  const setPendingFirstMessage = useConversationsStore((s) => s.setPendingFirstMessage)
  const setAwaitingResponse = useConversationsStore((s) => s.setAwaitingResponse)
  const addMessage = useConversationsStore((s) => s.addMessage)

  const textareaRef = useRef<PromptTextAreaHandle>(null)
  const attachmentsRef = useRef<AttachmentsContainerHandle>(null)

  useEffect(() => {
    if (promptMode === PromptMode.DOCUMENT_ANALYSIS) {
      attachmentsRef.current?.reset()
    }
  }, [promptMode])

  const handleConversationCreated = useCallback(
    (newId: string) => {
      const base = location.pathname.split('/plus')[0]
      navigate(`${base}/plus/${newId}`)
    },
    [location.pathname, navigate]
  )

  const { sendMessage } = useSocket({ onConversationCreated: handleConversationCreated })

  const handleSend = () => {
    const text = textareaRef.current?.getValue().trim()
    const uploadedFiles = attachmentsRef.current?.getFiles() ?? []

    const successfulAttachments = uploadedFiles
      .filter((f) => f.status === 'success' && f.uploadedUrl)
      .map((f) => f.uploadedUrl!)

    const attachmentFileNames = uploadedFiles
      .filter((f) => f.status === 'success' && f.uploadedUrl)
      .map((f) => f.file.name)

    if (!text) {
      toast.error('Please enter a message.')
      return
    }

    const attachmentsToBeSent = successfulAttachments.length > 0 ? successfulAttachments : undefined
    const attachmentNamesForStore = attachmentFileNames.length > 0 ? attachmentFileNames : undefined

    if (!currentConversationId) {
      const trackingId = randomUUID()
      setPendingFirstMessage({ text, attachments: attachmentNamesForStore, trackingId })

      sendMessage({
        trackingId,
        message: text,
        attachments: attachmentsToBeSent?.map((attachment) => ({ s3Uri: attachment })),
        chatMode: chatModeMap[promptMode],
      })
    } else {
      addMessage(currentConversationId, {
        id: randomUUID(),
        from: MessageSender.USER,
        text,
        attachments: attachmentNamesForStore,
        createdAt: Date.now(),
      })

      setAwaitingResponse(currentConversationId, true)

      sendMessage({
        conversationId: currentConversationId,
        parentMessageId,
        message: text,
        attachments: attachmentsToBeSent?.map((attachment) => ({ s3Uri: attachment })),
        chatMode: chatModeMap[promptMode],
      })
    }

    setPromptText('')
    textareaRef.current?.clear()
    attachmentsRef.current?.reset()
  }

  const chatModeMap: Record<PromptMode, ChatMode> = {
    [PromptMode.JOB_FOLDERS]: ChatMode.RETRIEVAL,
    [PromptMode.DOCUMENT_ANALYSIS]: ChatMode.CREATOR,
    [PromptMode.DATA_ANALYTICS]: ChatMode.RETRIEVAL,
  }

  const handleUploadClick = () => {
    attachmentsRef.current?.triggerFileDialog()
  }

  const isUploading = () => {
    const files = attachmentsRef.current?.getFiles?.() ?? []
    return files.some((f) => f.status === 'uploading')
  }

  const isSendButtonDisabled =
    !promptText.trim() ||
    isUploading() ||
    (!!pendingFirstMessage && !pendingFirstMessage.error) ||
    !!currentConversation?.isAwaitingResponse ||
    !isAuthorized

  return (
    <div className={classes.container}>
      <ActionButtonsRow mode={promptMode} onModeChange={setPromptMode} disabled={!isAuthorized} />

      <AttachmentsContainer ref={attachmentsRef} />

      <div className={classes.inputWrapper}>
        <PromptTextArea
          disabled={!isAuthorized}
          ref={textareaRef}
          value={promptText}
          onChange={setPromptText}
          onEnterPress={handleSend}
        />
        <Button
          className={classes.sendButton}
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={isSendButtonDisabled}
        >
          <Icon>send</Icon>
        </Button>
      </div>

      <div className={classes.disclaimer}>
        The AI model may not always provide accurate or relevant information. Please verify your
        results as needed.
      </div>

      <Collapse in={promptMode === PromptMode.DOCUMENT_ANALYSIS} timeout={350} unmountOnExit>
        <button
          className={classes.uploadButton}
          onClick={handleUploadClick}
          disabled={!isAuthorized}
        >
          <Icon>attach_file</Icon> Upload
        </button>
      </Collapse>
    </div>
  )
}

export default PromptInputArea
